import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  BookmarkTypeEnum,
  LessonI,
  ModuleI,
  PageI,
  ComponentI,
} from "../types";
import { useGetPageBookmarks } from "../api/queries";
import { useCreatePageBookmark, useDeletePageBookmark } from "../api/mutations";
import { useNotification } from "../hooks/useNotification";
import Button from "../componentLibrary/Button";
import Modal from "../componentLibrary/Modal";
import TutorialTip, {
  TutorialTipDirection,
} from "../componentLibrary/TutorialTip";
import { analytics } from "../services/AnalyticsService";
import { buildPageBookmarkPayload } from "../utils/buildPageBookmarkPayload";
import AddToToolboxBeforeIcon from "../assets/icons/AddToToolboxBefore.svg";
import AddToToolboxAfterIcon from "../assets/icons/AddToToolboxAfter.svg";
import ToolboxIcon from "../assets/icons/Toolbox.svg";

interface AddToToolboxButtonProps {
  page: PageI;
  module?: ModuleI;
  lesson?: LessonI;
  showTutorial?: boolean;
  tutorialPosition?: TutorialTipDirection;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  answers?: Record<string, any>;
  onBeforeNavigate?: () => void | Promise<void>;
}

const AddToToolboxButton: React.FC<AddToToolboxButtonProps> = ({
  page,
  module,
  lesson,
  showTutorial = true,
  tutorialPosition = "left",
  answers = {},
  onBeforeNavigate,
}) => {
  const navigate = useNavigate();
  const {
    data: pageBookmarks,
    isError,
    isLoading,
  } = useGetPageBookmarks(page._id);
  const queryClient = useQueryClient();
  const { notify, clearNotifications } = useNotification();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingBookmarkData, setPendingBookmarkData] = useState<ReturnType<
    typeof buildPageBookmarkPayload
  > | null>(null);

  const mutation = useCreatePageBookmark({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["bookmarks", BookmarkTypeEnum.PAGE],
      });

      // Build toolbox link - prioritize module-specific toolbox if moduleId exists
      const toolboxLink = module?._id
        ? `/my-toolboxes/${module._id}?tab=pages`
        : "/my-toolboxes?tab=pages";

      // Get current URL to use as return path - use window.location to ensure we get the most current URL including page parameter
      const currentUrl = window.location.pathname + window.location.search;

      const handleViewClick = async () => {
        clearNotifications();
        // Call the onBeforeNavigate callback if provided (to save page inputs)
        if (onBeforeNavigate) {
          await onBeforeNavigate();
        }
        navigate(toolboxLink, { state: { from: currentUrl } });
      };

      notify(
        "success",
        <>
          This page was added to <strong>My Pages</strong> in{" "}
          <strong>My Toolbox</strong>.{" "}
          <span
            className="underline font-bold hover:text-gray-700 cursor-pointer"
            onClick={handleViewClick}
          >
            View here.
          </span>
        </>,
      );
      analytics.track("toolbox_page_added", {
        category: "Toolbox",
        elementId: page._id,
        additionalDetails: {
          pageId: page._id,
          lessonId: lesson?._id,
          moduleId: module?._id,
        },
      });
    },
  });

  const deleteMut = useDeletePageBookmark({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["bookmarks", BookmarkTypeEnum.PAGE],
      });
      notify(
        "success",
        <>
          This page has been removed from <strong>My Pages</strong> in{" "}
          <strong>My Toolbox</strong>
        </>,
      );
    },
  });

  if (isLoading || isError || !pageBookmarks)
    return (
      <img
        src={AddToToolboxBeforeIcon}
        alt="add to toolbox icon"
        className="h-6 w-6"
      />
    );

  const confirmAddToToolbox = () => {
    if (pendingBookmarkData) {
      mutation.mutate(pendingBookmarkData);
      setShowConfirmModal(false);
      setPendingBookmarkData(null);
    }
  };

  const addToToolboxWithoutFrozenAnswers = () => {
    if (pendingBookmarkData) {
      mutation.mutate({
        ...pendingBookmarkData,
        frozenAnswers: {},
      });
      setShowConfirmModal(false);
      setPendingBookmarkData(null);
    }
  };

  const cancelAddToToolbox = () => {
    setShowConfirmModal(false);
    setPendingBookmarkData(null);
  };

  const toggleFavorite = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (pageBookmarks.length > 0) {
      deleteMut.mutate(pageBookmarks[0]._id);
    } else {
      const payload = buildPageBookmarkPayload(page, lesson, module, answers);

      // If there are frozen answers, show confirmation modal first
      if (Object.keys(payload.frozenAnswers).length > 0) {
        setPendingBookmarkData(payload);
        setShowConfirmModal(true);
      } else {
        mutation.mutate(payload);
      }
    }
  };

  const isAdded = pageBookmarks.length > 0;

  // Helper function to get label from component
  const getComponentLabel = (component: ComponentI): string => {
    return component.content?.label || component.content?.variable || "Unknown";
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formatAnswerValue = (answer: any): string => {
    if (!answer) return "No answer";

    const displayAs = answer.displayAs;
    const value = answer.value;

    if (displayAs) {
      return displayAs;
    }

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    return value || "No answer";
  };

  return (
    <>
      <TutorialTip
        tutorialId="favorites-button-tutorial"
        maxWidth={300}
        content={
          <div className="text-sm">
            <p className="font-medium mb-2">Save Your Favorite Pages</p>
            <p>
              When you find a page you want to revisit, click this icon{" "}
              <img
                src={AddToToolboxBeforeIcon}
                alt="add to toolbox icon"
                className="h-5 w-5 inline"
              />{" "}
              to add it to <br />
              <strong>My Pages</strong> in{" "}
              <img
                src={ToolboxIcon}
                alt="toolbox icon"
                className="h-4 w-4 inline"
              />{" "}
              <strong>My Toolbox</strong>
            </p>
          </div>
        }
        isVisible={showTutorial}
        direction={tutorialPosition}
      >
        <Button.Circle
          variant="secondary"
          className="h-10 w-10 flex items-center justify-center"
          onClick={toggleFavorite}
        >
          <img
            src={isAdded ? AddToToolboxAfterIcon : AddToToolboxBeforeIcon}
            alt={isAdded ? "remove from toolbox icon" : "add to toolbox icon"}
            className="w-8 h-8"
          />
        </Button.Circle>
      </TutorialTip>

      {showConfirmModal && pendingBookmarkData && (
        <Modal
          title="Do You Want to Save Your Answers?"
          subtitle="The following answers will be saved with this page in your toolbox:"
          isOpen={showConfirmModal}
          onCloseModal={cancelAddToToolbox}
          size="lg"
          height="auto"
          maxHeight="60vh"
          position="bottom"
          footer={
            <div className="flex justify-between items-center gap-3">
              <Button
                variant="secondary"
                onClick={cancelAddToToolbox}
                className="h-auto"
                size="sm"
              >
                Cancel
              </Button>
              <div className="flex gap-3">
                <Button
                  variant="cautionary"
                  onClick={addToToolboxWithoutFrozenAnswers}
                  className="h-auto"
                  size="sm"
                >
                  Save Blank Page
                </Button>
                <Button
                  variant="primary"
                  onClick={confirmAddToToolbox}
                  className="h-auto"
                  size="sm"
                >
                  Save With Answers
                </Button>
              </div>
            </div>
          }
        >
          <div className="space-y-4 py-4">
            {pendingBookmarkData.frozenComponents
              .filter((component) => {
                const variable = component.content?.variable;
                return variable && pendingBookmarkData.frozenAnswers[variable];
              })
              .map((component, index) => {
                const variable = component.content?.variable;
                const answer = variable
                  ? pendingBookmarkData.frozenAnswers[variable]
                  : undefined;

                return (
                  <div
                    key={variable || index}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  >
                    <div className="font-semibold text-gray-900 mb-2">
                      {getComponentLabel(component)}
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">Answer: </span>
                      <span className="whitespace-pre-wrap">
                        {formatAnswerValue(answer)}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </Modal>
      )}
    </>
  );
};

export default AddToToolboxButton;
